import { AnimatePresence, motion, useMotionValue, useAnimation } from "motion/react";
import { useEffect, useMemo, useState, useRef } from "react";
import { FaStar } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@server";
import { trpc } from "~/api/trpc";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import TopProgressBar from "~/components/new/TopProgressBar";
import { DayOfWeek, SelectedStore, useBookingFlowStore } from "~/stores/booking-flow-store";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import { Language, useLanguageStore } from "~/stores/language-store";
import { GTMEvent } from "../../components/gtmProvider/GTMContext";
import useGTM from "../../components/gtmProvider/useGTM";
import { ArrowRight, LogOut } from "lucide-react";

// Image carousel component for selected store
type ImageCarouselProps = {
    images: string[];
}
const ImageCarousel = ({ images }: ImageCarouselProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const x = useMotionValue(0);
    const controls = useAnimation();

    // Animate to current index when it changes
    useEffect(() => {
        if (!containerRef.current) return;
        const slideWidth = containerRef.current.offsetWidth;
        controls.start({ 
            x: -currentIndex * slideWidth,
            transition: { type: "spring", stiffness: 300, damping: 30 }
        });
    }, [currentIndex, controls]);

    if (!images || images.length === 0) {
        return null;
    }

    const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number }; velocity: { x: number } }) => {
        if (!containerRef.current) return;
        
        const slideWidth = containerRef.current.offsetWidth;
        const swipeThreshold = slideWidth * 0.2; // 20% of slide width
        const velocityThreshold = 500;
        
        const swipedLeft = info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold;
        const swipedRight = info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold;
        
        let newIndex = currentIndex;
        if (swipedLeft && currentIndex < images.length - 1) {
            newIndex = currentIndex + 1;
        } else if (swipedRight && currentIndex > 0) {
            newIndex = currentIndex - 1;
        }
        
        setCurrentIndex(newIndex);
        // Animate to the new position
        controls.start({ 
            x: -newIndex * slideWidth,
            transition: { type: "spring", stiffness: 300, damping: 30 }
        });
    };

    const goToIndex = (idx: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentIndex(idx);
    };

    return (
        <div className='flex flex-col gap-3 mt-4'>
            <div ref={containerRef} className='w-full h-48 md:h-64 overflow-hidden rounded-lg relative'>
                <motion.div
                    className='flex h-full cursor-grab active:cursor-grabbing'
                    drag="x"
                    dragConstraints={containerRef}
                    dragElastic={0.1}
                    onDragEnd={handleDragEnd}
                    animate={controls}
                    style={{ x, width: `${images.length * 100}%` }}
                >
                    {images.map((src, idx) => (
                        <div 
                            key={idx} 
                            className='h-full shrink-0'
                            style={{ width: `${100 / images.length}%` }}
                        >
                            <img
                                className='w-full h-full object-cover pointer-events-none'
                                src={src}
                                alt={`Clinic image ${idx + 1}`}
                                draggable={false}
                            />
                        </div>
                    ))}
                </motion.div>
                {/* Dot indicators overlaid on image */}
                {images.length > 1 && (
                    <div className='absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10'>
                        {images.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={(e) => goToIndex(idx, e)}
                                className={`h-2 rounded-full transition-all duration-300 ${idx === currentIndex
                                    ? 'w-6 bg-white'
                                    : 'w-2 bg-white/60 hover:bg-white/80'
                                    }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Radio button indicator component
const RadioIndicator = ({ isSelected }: { isSelected: boolean }) => {
    return (
        <div className={`w-5 h-5 rounded-full border-3 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'border-burgundy' : 'border-burgundy/20'
            }`}>
            {isSelected && (
                <div className='w-5 h-5 rounded-full bg-burgundy' />
            )}
        </div>
    );
};

type StoreCardProps = {
    store: SelectedStore;
    isSelected: boolean;
    onSelect: () => void;
}
const StoreCard = ({ store, isSelected, onSelect }: StoreCardProps) => {
    // Collect all available images for the carousel
    const storeImages = useMemo(() => {
        const imgs = [...(store.images ?? [])];
        if (store.clinicGoogleMapsImgSrc) imgs.push(store.clinicGoogleMapsImgSrc);
        return imgs;
    }, [store]);

    return (
        <div className='flex flex-col'>
            <div
                className={`flex flex-col rounded-[5px] bg-white border-2 transition-all duration-200 cursor-pointer drop-shadow-md ${isSelected ? 'border-burgundy' : 'border-burgundy/20'}`}
                onClick={onSelect}
            >
                <div className='flex flex-row justify-between items-center gap-[10px] p-[15px]'>
                    <div className='flex flex-col'>
                        <span className='font-bold text-base text-burgundy'>{store.name}</span>
                        <span className='text-sm text-burgundy'>{store.address}, {store.zipCode}, {store.city}</span>
                    </div>
                    <RadioIndicator isSelected={isSelected} />
                </div>
            </div>

            {/* Image carousel - only shown when selected, with smooth animation */}
            <AnimatePresence initial={false}>
                {isSelected && storeImages.length > 0 && (
                    <motion.div
                        key="carousel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className='overflow-hidden'
                    >
                        <div className='pb-4'>
                            <ImageCarousel images={storeImages} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const StoreCardSkeleton = () => {
    return (
        <div className='flex flex-row justify-between items-center gap-3 p-4 rounded-xl bg-white border-2 border-gray-200'>
            <div className='flex flex-col gap-2 flex-1'>
                <div className='h-5 bg-gray-200 rounded animate-pulse w-32'></div>
                <div className='h-4 bg-gray-200 rounded animate-pulse w-48'></div>
            </div>
            <div className='w-5 h-5 rounded-full bg-gray-200 animate-pulse'></div>
        </div>
    );
};

// Google rating component
const GoogleRating = ({ rating }: { rating: number }) => {
    const { t } = useLanguageStore();
    const locale = t('locale') as string;
    
    const formattedRating = rating.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    
    return (
        <div className='flex items-center justify-center gap-2'>
            <img src="https://www.gstatic.com/images/branding/product/1x/googleg_48dp.png" alt="Google" className='w-5 h-5' />
            <span className='text-sm text-[#454545]'>{formattedRating}</span>
            <div className='flex gap-0.5'>
                {Array.from({ length: 5 }).map((_, idx) => (
                    <FaStar key={idx} className='text-[#F9D252] w-4 h-4' />
                ))}
            </div>
        </div>
    );
};

// Fixed bottom bar with login prompt and continue button
type FixedBottomBarProps = {
    selectedStore: SelectedStore | null;
    continueButtonText: string;
    onContinue: () => void;
    onLoginClick: () => void;
    onLogout: () => void;
};

const FixedBottomBar = ({ selectedStore, continueButtonText, onContinue, onLoginClick, onLogout }: FixedBottomBarProps) => {
    const { t } = useLanguageStore();
    const { customer, isAuthenticated } = useCustomerAuthStore();

    return (
        <div className="fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-3 px-6 pt-4 pb-6 bg-light-burgundy">
            {/* Login prompt or logged-in state */}
            {isAuthenticated() && customer ? (
                <div className='flex items-center justify-center gap-2 text-xs text-burgundy font-light'>
                    <span>{t('loggedInAs')} {customer.firstName}</span>
                    <button
                        onClick={onLogout}
                        className='flex items-center gap-1 text-burgundy/70 hover:text-burgundy transition-colors underline'
                    >
                        <LogOut className='w-3 h-3' />
                        {t('logout')}
                    </button>
                </div>
            ) : (
                <p className='text-xs text-center text-burgundy font-light'>
                    {t('loginPromptText')}{' '}
                    <button
                        onClick={onLoginClick}
                        className='text-burgundy underline hover:text-burgundy/70 transition-colors'
                    >
                        {t('loginPromptLink')}
                    </button>
                </p>
            )}
            <PrimaryActionButton
                onClick={onContinue}
                label={continueButtonText}
                disabled={!selectedStore}
                rightIcon={<ArrowRight />}
            />
        </div>
    );
};

/** Returns the distance in km between two lat/lng points using the Haversine formula. */
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

type StoreSelectionConfigOutput = inferRouterOutputs<AppRouter>['publicBookingFunnel']['getStoreSelectionConfig'];
export type StoreSelectionCmsConfig = StoreSelectionConfigOutput['config'];
export type StoreSelectionCmsStore = StoreSelectionConfigOutput['stores'][number];

export const mapStoreToSelectedStore = (store: StoreSelectionCmsStore): SelectedStore => {
    const daysOfWeek: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const daysOpening: Record<DayOfWeek, { openingTime: string; closingTime: string; } | null> = {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null
    };
    daysOfWeek.forEach((dayOfWeek) => {
        const startEnd: { start: string; end: string; }[] | null = !!store[dayOfWeek] ? JSON.parse(store[dayOfWeek]) : null;
        if (startEnd === null) {
            daysOpening[dayOfWeek] = null;
            return;
        }
        const earliestStart = startEnd.sort((a, b) => parseInt(a.start.replace(/\D/g, "")) - parseInt(b.start.replace(/\D/g, "")))[0].start;
        const latestEnd = startEnd.sort((a, b) => parseInt(b.end.replace(/\D/g, "")) - parseInt(a.end.replace(/\D/g, "")))[0].end;
        daysOpening[dayOfWeek] = {
            openingTime: earliestStart,
            closingTime: latestEnd,
        };
    });
    daysOpening.sunday = null; // hard-code sunday closed
    return {
        id: store.id,
        slug: store.slug || `${store.id}`,
        name: store.name,
        contactEmail: store.contactEmail,
        contactPhone: store.contactPhone,
        description: store.description,
        fullAddress: store.fullAddressForEmails ?? undefined,
        address: store.address,
        city: store.city,
        zipCode: store.zipCode,

        openingTimes: daysOpening,

        reviewsStarsOutOfFive: store.reviewsStarsOutOfFive ? parseFloat(String(store.reviewsStarsOutOfFive)) : undefined,
        reviewsCount: store.reviewsCount,
        images: store.images,
        clinicGoogleMapsImgSrc: store.clinicGoogleMapsImgSrc,
        clinicGoogleMapsUrl: store.googleMapsUrl ?? undefined,
        federalState: store.federalState ?? null,
        locationLatitude: store.locationLatitude,
        locationLongitude: store.locationLongitude,
    };
};

/**
 * Lets the deeplink route render this page with its own (already resolved) store list,
 * progress values and continue handler instead of the standard funnel's.
 */
export type DeeplinkStoreSelectionContext = {
    isLoading: boolean;
    config: StoreSelectionCmsConfig | null;
    stores: SelectedStore[];
    percentageFilled: number;
    progressFallbackDe: string;
    progressFallbackEn: string;
    onContinue: (store: SelectedStore) => void;
};

type StoreSelectionProps = {
    deeplink?: DeeplinkStoreSelectionContext;
};

const StoreSelection = ({ deeplink }: StoreSelectionProps) => {
    const { sendEvent } = useGTM();

    const navigate = useNavigate();
    const location = useLocation();
    const {
        selectedStore, setSelectedStore, setDeeplinkSlug, setDeeplinkPractitionerId,
        setDeeplinkCategoryId, setDeeplinkTargetCategoryId,
    } = useBookingFlowStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            setSelectedStore: state.setSelectedStore,
            setDeeplinkSlug: state.setDeeplinkSlug,
            setDeeplinkPractitionerId: state.setDeeplinkPractitionerId,
            setDeeplinkCategoryId: state.setDeeplinkCategoryId,
            setDeeplinkTargetCategoryId: state.setDeeplinkTargetCategoryId,
        }))
    );
    const { t, language } = useLanguageStore();
    const { customer, isAuthenticated, logout, startLogin } = useCustomerAuthStore();

    const handleLoginClick = () => {
        // Save current path so we can return after login
        startLogin(location.pathname + location.search);
        navigate("/login");
    };

    const handleLogout = () => {
        logout();
    };

    // Fetch CMS config and stores (the deeplink route resolves both itself)
    const { data: cmsData, isLoading: isLoadingCms } = trpc.publicBookingFunnel.getStoreSelectionConfig.useQuery(undefined, {
        staleTime: 60000, // 1 minute cache
        enabled: !deeplink,
    });
    const config = deeplink ? deeplink.config : cmsData?.config;
    const isLoading = deeplink ? deeplink.isLoading : isLoadingCms;
    const storesFromCMS = cmsData?.stores || [];

    useEffect(() => {
        window.scroll(0, 0);
        // A deeplink session sets its own slug/practitioner and fires its own init event
        // once the slug resolves, so clearing them here would untag its analytics.
        if (deeplink) return;
        setDeeplinkSlug(null);
        setDeeplinkPractitionerId(null);
        setDeeplinkCategoryId(null);
        setDeeplinkTargetCategoryId(null);
        sendEvent(GTMEvent.BOOKING_FUNNEL_INIT, {
            user: {
                id: null,
                has_account: false,
                email: null,
                phone: null,
            },
        });
    }, []);

    // Sort stores alphabetically by name
    const sortedStores = useMemo(() => {
        if (storesFromCMS && storesFromCMS.length > 0) {
            return [...storesFromCMS].sort((a, b) =>
                a.name.localeCompare(b.name)
            );
        }
        return [];
    }, [storesFromCMS]);

    const storeData: SelectedStore[] = useMemo(() => {
        if (deeplink) {
            return deeplink.stores;
        }
        if (!sortedStores || sortedStores.length === 0) {
            return [];
        }

        // Don't include digitalStore for now - only physical stores
        return sortedStores.map(mapStoreToSelectedStore);
    }, [sortedStores, deeplink?.stores]);

    useEffect(() => {
        if (deeplink) {
            return;
        }
        if (!sortedStores || sortedStores.length === 0) {
            return;
        }
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("deeplink")) {
            // Handle deeplink
        } else if (urlParams.get("store")) {
            const storeKey = urlParams.get("store");
            const store = storeData.find((s) => s.slug === storeKey);
            if (store) {
                setSelectedStore(store);
                sendEvent(GTMEvent.BOOKING_FUNNEL_BRANCH_SELECTED, {
                    branch: { id: store.id, name: store.name },
                });
                navigate("/select-path");
            }
        }
    }, [sortedStores]);

    // Preselect the nearest store based on IP geolocation
    const hasUserManuallySelected = useRef(false);
    const hasGeolocated = useRef(false);
    const { data: locationData } = trpc.publicUtilities.getMyLocation.useQuery(undefined, {
        staleTime: Infinity,
        enabled: storeData.length > 0,
    });

    useEffect(() => {
        if (hasGeolocated.current || hasUserManuallySelected.current) return;
        if (selectedStore) return;
        if (!locationData?.latitude || !locationData?.longitude) return;
        if (storeData.length === 0) return;

        const storesWithCoords = storeData.filter(
            (s): s is SelectedStore & { locationLatitude: number; locationLongitude: number } =>
                s.locationLatitude != null &&
                s.locationLongitude != null &&
                (s.locationLatitude !== 0 || s.locationLongitude !== 0)
        );
        if (storesWithCoords.length === 0) return;

        let nearestStore: (typeof storesWithCoords)[0] | null = null;
        let minDistance = Infinity;

        for (const store of storesWithCoords) {
            const dist = haversineDistance(locationData.latitude, locationData.longitude, store.locationLatitude, store.locationLongitude);
            if (dist < minDistance) {
                minDistance = dist;
                nearestStore = store;
            }
        }

        if (nearestStore) {
            hasGeolocated.current = true;
            setSelectedStore(nearestStore);
        }
    }, [locationData, storeData, selectedStore]);

    const handleStoreSelect = (store: SelectedStore) => {
        hasUserManuallySelected.current = true;
        if (selectedStore?.id === store.id) {
            setSelectedStore(null);
        } else {
            setSelectedStore(store);
        }
    };

    const handleContinue = () => {
        if (!selectedStore) {
            return;
        }
        if (deeplink) {
            deeplink.onContinue(selectedStore);
            return;
        }
        sendEvent(GTMEvent.BOOKING_FUNNEL_BRANCH_SELECTED, {
            branch: { id: selectedStore.id, name: selectedStore.name },
        });
        navigate("/select-path");
    };

    // Get localized text from CMS config
    const pageTitle = language === Language.GERMAN 
        ? (config?.pageTitleDe || t('location')) 
        : (config?.pageTitleEn || t('location'));
    const progressDisplay = language === Language.GERMAN 
        ? (config?.progressDisplayDe || deeplink?.progressFallbackDe || 'SCHRITT 1 / 5') 
        : (config?.progressDisplayEn || deeplink?.progressFallbackEn || 'STEP 1 / 5');
    const loginPromptText = language === Language.GERMAN 
        ? (config?.loginPromptTextDe || 'Du warst schon mal bei uns?') 
        : (config?.loginPromptTextEn || 'Have you been here before?');
    const loginPromptLinkText = language === Language.GERMAN 
        ? (config?.loginPromptLinkTextDe || 'Klicke hier um dich einzuloggen.') 
        : (config?.loginPromptLinkTextEn || 'Click here to login.');
    const continueButtonText = language === Language.GERMAN 
        ? (config?.continueButtonTextDe || 'WEITER') 
        : (config?.continueButtonTextEn || 'CONTINUE');
    const googleRatingValue = config?.googleRatingValue ? Number(config.googleRatingValue) : 4.9;
    const showGoogleRating = config?.showGoogleRating ?? true;

    return (
        <motion.div
            className='flex flex-col min-h-screen'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <TopProgressBar percentageFilled={deeplink?.percentageFilled ?? 20} displayString={progressDisplay} onClickBackButton={() => { }} />

            {/* Title */}
            <motion.div
                className='flex flex-col items-center gap-4 px-6 py-6'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
            >
                {isLoading ? (
                    <>
                        <div className='h-8 w-48 bg-burgundy/10 rounded animate-pulse' />
                        <div className='h-5 w-32 bg-burgundy/10 rounded animate-pulse' />
                    </>
                ) : (
                    <>
                        <h1 className='text-2xl text-burgundy text-center'>
                            {pageTitle}
                        </h1>
                        {showGoogleRating && <GoogleRating rating={googleRatingValue} />}
                    </>
                )}
            </motion.div>

            {/* Store list - pb-32 provides space for fixed bottom section */}
            <div className='flex-1 overflow-y-auto px-6 pb-32'>
                <div className='flex flex-col gap-3'>
                    {isLoading ? (
                        Array.from({ length: 4 }).map((_, idx) => (
                            <StoreCardSkeleton key={idx} />
                        ))
                    ) : (
                        storeData.map((store, index) => (
                            <motion.div
                                key={`store-${store.id}-${store.city}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 + index * 0.08, ease: "easeOut" }}
                            >
                                <StoreCard
                                    store={store}
                                    isSelected={selectedStore?.id === store.id}
                                    onSelect={() => handleStoreSelect(store)}
                                />
                            </motion.div>
                        ))
                    )}
                </div>
            </div>

            <FixedBottomBar
                selectedStore={selectedStore}
                continueButtonText={continueButtonText}
                onContinue={handleContinue}
                onLoginClick={handleLoginClick}
                onLogout={handleLogout}
            />
        </motion.div>
    );
};

export default StoreSelection;